import axios from 'axios';

// Create a dedicated axios instance with the correct base URL
const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://sakha-cms.sakhaai.test'
});

// Request interceptor to attach auth headers
apiClient.interceptors.request.use(
  (config) => {
    // Attach access token
    const accessToken = localStorage.getItem('access_token');
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    // Attach tenant and project IDs from localStorage
    const tenantId = localStorage.getItem('tenant_id');
    const projectId = localStorage.getItem('project_id');

    if (tenantId) {
      config.headers['X-Tenant-Id'] = tenantId;
    }
    if (projectId) {
      config.headers['X-Project-Id'] = projectId;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Auto-refresh on 401
let refreshPromise = null;

async function tryRefresh() {
  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) return null;
  try {
    const res = await axios.post(
      `${apiClient.defaults.baseURL}/api/auth/refresh`,
      { refreshToken },
      { headers: { 'Content-Type': 'application/json' } }
    );
    const payload = res.data?.data ?? res.data;
    if (payload?.accessToken) {
      localStorage.setItem('access_token', payload.accessToken);
      if (payload.refreshToken) localStorage.setItem('refresh_token', payload.refreshToken);
      return payload.accessToken;
    }
    return null;
  } catch {
    return null;
  }
}

apiClient.interceptors.response.use(
  (response) => {
    // Unwrap envelope: { success, data } → return data
    if (response.data && typeof response.data === 'object' && 'success' in response.data) {
      if (!response.data.success) {
        return Promise.reject(new Error(response.data.error?.message || 'Request failed'));
      }
      response.data = response.data.data;
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry && localStorage.getItem('refresh_token')) {
      originalRequest._retry = true;
      if (!refreshPromise) {
        refreshPromise = tryRefresh().finally(() => { refreshPromise = null; });
      }
      const newToken = await refreshPromise;
      if (newToken) {
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return apiClient(originalRequest);
      }
    }
    if (error.response?.status === 401) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      localStorage.removeItem('tenant');
      localStorage.removeItem('tenant_id');
      localStorage.removeItem('project_id');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

class ApiService {
  // Pages API
  static async getPages(params = {}) {
    const response = await apiClient.get('/api/pages', { params });
    return response.data;
  }

  static async getPageById(id) {
    const response = await apiClient.get(`/api/pages/${id}`);
    return response.data;
  }

  static async createPage(pageData) {
    const response = await apiClient.post('/api/pages', pageData);
    return response.data;
  }

  static async updatePage(id, pageData) {
    const response = await apiClient.put(`/api/pages/${id}`, pageData);
    return response.data;
  }

  static async deletePage(id) {
    const response = await apiClient.delete(`/api/pages/${id}`);
    return response.data;
  }

  static async duplicatePage(id, data) {
    const response = await apiClient.post(`/api/pages/${id}/duplicate`, data);
    return response.data;
  }

  static async publishPage(id) {
    const response = await apiClient.put(`/api/pages/${id}/publish`);
    return response.data;
  }

  static async unpublishPage(id) {
    const response = await apiClient.put(`/api/pages/${id}/unpublish`);
    return response.data;
  }

  // Page Sections API
  static async getPageSections(pageId) {
    const response = await apiClient.get(`/api/pages/${pageId}/sections`);
    return response.data;
  }

  static async createSection(pageId, sectionData) {
    const response = await apiClient.post(`/api/pages/${pageId}/sections`, sectionData);
    return response.data;
  }

  static async updateSection(pageId, sectionId, sectionData) {
    const response = await apiClient.put(`/api/pages/${pageId}/sections/${sectionId}`, sectionData);
    return response.data;
  }

  static async deleteSection(pageId, sectionId) {
    const response = await apiClient.delete(`/api/pages/${pageId}/sections/${sectionId}`);
    return response.data;
  }

  static async reorderSections(pageId, sections) {
    const response = await apiClient.put(`/api/pages/${pageId}/sections/reorder`, { sections });
    return response.data;
  }

  static async updatePageSections(pageId, sections) {
    const response = await apiClient.put(`/api/pages/${pageId}/sections`, { sections });
    return response.data;
  }

  // Media API
  static async getMedia(params = {}) {
    const response = await apiClient.get(`/api/media`, { params });
    return response.data;
  }

  static async uploadMedia(formData) {
    const response = await apiClient.post(`/api/media/presign-upload`, formData);
    return response.data;
  }

  static async registerMedia(data) {
    const response = await apiClient.post(`/api/media/register`, data);
    return response.data;
  }

  static async updateMedia(id, data) {
    const response = await apiClient.put(`/api/media/${id}`, data);
    return response.data;
  }

  static async deleteMedia(id) {
    const response = await apiClient.delete(`/api/media/${id}`);
    return response.data;
  }

  static async bulkDeleteMedia(ids) {
    const response = await apiClient.post(`/api/media/bulk-delete`, { ids });
    return response.data;
  }

  static async getMediaStats() {
    const response = await apiClient.get(`/api/media/stats`);
    return response.data;
  }

  // New S3 Upload Methods
  static async generateUploadUrl(uploadData) {
    const response = await apiClient.post(`/api/media/generate-upload-url`, uploadData);
    return response.data;
  }

  static async confirmUpload(confirmData) {
    const response = await apiClient.post(`/api/media/confirm-upload`, confirmData);
    return response.data;
  }

  static async uploadWithThumbnail(formData) {
    const response = await apiClient.post(`/api/media/upload-with-thumbnail`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  }

  // Blog API
  static async getBlogPosts(params = {}) {
    const response = await apiClient.get(`/api/blog/posts`, { params });
    return response.data;
  }

  static async getBlogPost(id) {
    const response = await apiClient.get(`/api/blog/posts/${id}`);
    return response.data;
  }

  static async createBlogPost(postData) {
    const response = await apiClient.post(`/api/blog/posts`, postData);
    return response.data;
  }

  static async updateBlogPost(id, postData) {
    const response = await apiClient.put(`/api/blog/posts/${id}`, postData);
    return response.data;
  }

  static async deleteBlogPost(id) {
    const response = await apiClient.delete(`/api/blog/posts/${id}`);
    return response.data;
  }

  static async publishBlogPost(id) {
    const response = await apiClient.put(`/api/blog/posts/${id}/publish`);
    return response.data;
  }

  static async unpublishBlogPost(id) {
    const response = await apiClient.put(`/api/blog/posts/${id}/unpublish`);
    return response.data;
  }

  // Blog Categories
  static async getBlogCategories() {
    const response = await apiClient.get(`/api/blog/categories`);
    return response.data;
  }

  static async createBlogCategory(categoryData) {
    const response = await apiClient.post(`/api/blog/categories`, categoryData);
    return response.data;
  }

  // Blog Authors
  static async getBlogAuthors() {
    const response = await apiClient.get(`/api/blog/authors`);
    return response.data;
  }

  // Blog Stats
  static async getBlogStats() {
    const response = await apiClient.get(`/api/blog/stats`);
    return response.data;
  }

  // Events API
  static async getEvents(params = {}) {
    const response = await apiClient.get(`/api/events`, { params });
    return response.data;
  }

  static async getEvent(id) {
    const response = await apiClient.get(`/api/events/${id}`);
    return response.data;
  }

  static async createEvent(eventData) {
    const response = await apiClient.post(`/api/events`, eventData);
    return response.data;
  }

  static async updateEvent(id, eventData) {
    const response = await apiClient.put(`/api/events/${id}`, eventData);
    return response.data;
  }

  static async deleteEvent(id) {
    const response = await apiClient.delete(`/api/events/${id}`);
    return response.data;
  }

  // Event Categories
  static async getEventCategories() {
    const response = await apiClient.get(`/api/events/categories/list`);
    return response.data;
  }

  // Event Organizers
  static async getEventOrganizers() {
    const response = await apiClient.get(`/api/events/organizers`);
    return response.data;
  }

  // Event Stats
  static async getEventStats() {
    const response = await apiClient.get(`/api/events/stats/overview`);
    return response.data;
  }

  // Team API
  static async getTeamMembers(params = {}) {
    const response = await apiClient.get(`/api/team`, { params });
    return response.data;
  }

  static async getTeamMember(id) {
    const response = await apiClient.get(`/api/team/${id}`);
    return response.data;
  }

  static async createTeamMember(memberData) {
    const response = await apiClient.post(`/api/team`, memberData);
    return response.data;
  }

  static async updateTeamMember(id, memberData) {
    const response = await apiClient.put(`/api/team/${id}`, memberData);
    return response.data;
  }

  static async deleteTeamMember(id) {
    const response = await apiClient.delete(`/api/team/${id}`);
    return response.data;
  }

  // Team Categories/Designations API
  static async getTeamCategories() {
    const response = await apiClient.get(`/api/team/categories/list`);
    return response.data;
  }

  static async getTeamStats() {
    const response = await apiClient.get(`/api/team/stats`);
    return response.data;
  }

  // Team Designations API
  static async getTeamDesignations(params = {}) {
    const response = await apiClient.get(`/api/tenant-designations`, { params });
    return response.data;
  }

  static async getTeamDesignation(id) {
    const response = await apiClient.get(`/api/tenant-designations/${id}`);
    return response.data;
  }

  static async createTeamDesignation(designationData) {
    const response = await apiClient.post(`/api/tenant-designations`, designationData);
    return response.data;
  }

  static async updateTeamDesignation(id, designationData) {
    const response = await apiClient.put(`/api/tenant-designations/${id}`, designationData);
    return response.data;
  }

  static async deleteTeamDesignation(id) {
    const response = await apiClient.delete(`/api/tenant-designations/${id}`);
    return response.data;
  }

  static async reorderTeamDesignations(designations) {
    const response = await apiClient.post(`/api/tenant-designations/reorder`, { designations });
    return response.data;
  }

  static async getDesignationLevels() {
    const response = await apiClient.get(`/api/tenant-designations/levels`);
    return response.data;
  }

  // Dashboard API
  static async getDashboardOverview() {
    const response = await apiClient.get(`/api/tenant-dashboard/overview`);
    return response.data;
  }

  static async getDashboardStatistics() {
    const response = await apiClient.get(`/api/tenant-dashboard/statistics`);
    return response.data;
  }

  // Settings API
  static async getSettings() {
    const response = await apiClient.get(`/api/settings`);
    return response.data;
  }

  static async updateSettings(settings) {
    const response = await apiClient.put(`/api/settings`, settings);
    return response.data;
  }

  static async getImpactStats() {
    const response = await apiClient.get(`/api/settings/impact-stats`);
    return response.data;
  }

  static async createImpactStat(statData) {
    const response = await apiClient.post(`/api/settings/impact-stats`, statData);
    return response.data;
  }

  static async updateImpactStat(id, statData) {
    const response = await apiClient.put(`/api/settings/impact-stats/${id}`, statData);
    return response.data;
  }

  static async deleteImpactStat(id) {
    const response = await apiClient.delete(`/api/settings/impact-stats/${id}`);
    return response.data;
  }

  // Enterprise Dashboard API
  static async getEnterpriseDashboardOverview(period = 'last_30_days') {
    const response = await apiClient.get(`/api/enterprise-dashboard/overview`, {
      params: { period }
    });
    return response.data;
  }

  static async getEnterpriseKPIs(period = 'last_30_days') {
    const response = await apiClient.get(`/api/enterprise-dashboard/kpis`, {
      params: { period }
    });
    return response.data;
  }

  static async getRealTimeStats() {
    const response = await apiClient.get(`/api/enterprise-dashboard/realtime`);
    return response.data;
  }

  static async getSystemHealth() {
    const response = await apiClient.get(`/api/enterprise-dashboard/system-health`);
    return response.data;
  }

  // Donations API
  static async getCampaigns(params = {}) {
    const response = await apiClient.get(`/api/donations/campaigns`, { params });
    return response.data;
  }

  static async getCampaign(id) {
    const response = await apiClient.get(`/api/donations/campaigns/${id}`);
    return response.data;
  }

  static async createCampaign(campaignData) {
    const response = await apiClient.post(`/api/donations/campaigns`, campaignData);
    return response.data;
  }

  static async updateCampaign(id, campaignData) {
    const response = await apiClient.put(`/api/donations/campaigns/${id}`, campaignData);
    return response.data;
  }

  static async deleteCampaign(id) {
    const response = await apiClient.delete(`/api/donations/campaigns/${id}`);
    return response.data;
  }

  static async getDonations(params = {}) {
    const response = await apiClient.get(`/api/donations`, { params });
    return response.data;
  }

  static async getDonationStats() {
    const response = await apiClient.get(`/api/donations/stats/overview`);
    return response.data;
  }

  // Generic HTTP methods for user management and other dynamic endpoints
  static async get(endpoint, config = {}) {
    const response = await apiClient.get(endpoint, config);
    return response.data;
  }

  static async post(endpoint, data = {}, config = {}) {
    const response = await apiClient.post(endpoint, data, config);
    return response.data;
  }

  static async put(endpoint, data = {}, config = {}) {
    const response = await apiClient.put(endpoint, data, config);
    return response.data;
  }

  static async delete(endpoint, config = {}) {
    const response = await apiClient.delete(endpoint, config);
    return response.data;
  }
}

export { apiClient };
export default ApiService;